import { useMutation } from '@tanstack/react-query'
import { Alert, Flex, Form, Input, Modal, App as AntdApp } from 'antd'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { changePassword } from './changePasswordApi'

interface FormValues {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

/**
 * Parolni almashtirish — bosh navigatsiyada alohida bo'lim EMAS (vazifa
 * hujjati: bor-yo'g'i besh nav elementi), header'dagi profil tugmasidan
 * ochiladigan modal sifatida (`AppHeader.tsx`).
 */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<FormValues>()

  /*
    `OperationResult` UCH holatli (loyihaning umumiy qoidasi,
    `operationResult.ts` dagi izoh): `OK` — yashil, `PARTIAL` — sariq
    ogohlantirish (baza yangilandi, lekin bu holatda amaliy oqibati yo'q —
    parol allaqachon saqlangan, shunchaki xabar bilan farqlanadi),
    `FAILED` — bazaga umuman yozilmadi, oyna YOPILMAYDI.

    ⚠️ Xato ALOHIDA `useState` da EMAS, mutatsiyaning o'zida
    (`VoucherVoidModal` naqshi, admin panel). Ikkinchi nusxa har
    ochish-yopishda qo'lda tozalanishi kerak edi va bitta unutilgan joy
    keyingi ochilishda BEGONA xabar ko'rsatardi — abonent hali hech narsa
    bosmagan bo'lsa ham. Naqshning uch oyog'i: `onError` yo'q, xabar
    oynaning ichida, yopishda `reset()`.

    ⚠️ Ikki xil xato ikki manbadan keladi va ikkalasi ham kerak:
    tashlangan istisno (`mutation.error`, masalan 401 yoki tarmoq) va
    HTTP 200 bilan qaytgan `FAILED` (`mutation.data`) — ikkinchisi
    react-query uchun xato EMAS.
  */
  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (result) => {
      if (result.status === 'FAILED') return
      if (result.status === 'PARTIAL') {
        void message.warning(result.message ?? t('account.changePassword.success'))
      } else {
        void message.success(result.message ?? t('account.changePassword.success'))
      }
      form.resetFields()
      onClose()
    },
  })

  /** Yopish — eski xabar keyingi ochilishga o'tib qolmasin */
  const close = () => {
    if (mutation.isPending) return
    form.resetFields()
    mutation.reset()
    onClose()
  }

  const handleFinish = (values: FormValues) => {
    mutation.mutate({ currentPassword: values.currentPassword, newPassword: values.newPassword })
  }

  const failed = mutation.data?.status === 'FAILED' ? mutation.data : null

  return (
    <Modal
      open={open}
      title={t('account.changePassword.title')}
      onCancel={close}
      onOk={() => form.submit()}
      okText={mutation.isPending ? t('account.changePassword.submitting') : t('account.changePassword.submit')}
      confirmLoading={mutation.isPending}
      cancelButtonProps={{ disabled: mutation.isPending }}
      destroyOnHidden
    >
      {/* Bo'sh joy qoldirmaslik uchun konteyner faqat xabar BORLIGIDA chiziladi */}
      {mutation.isError || failed ? (
        <Flex vertical gap={12} style={{ marginBottom: 16 }}>
          {mutation.isError ? (
            <Alert type="error" showIcon message={translateError(mutation.error, t)} />
          ) : null}
          {failed ? (
            <Alert
              type="error"
              showIcon
              message={failed.message ?? t('account.changePassword.failed')}
            />
          ) : null}
        </Flex>
      ) : null}

      <Form form={form} layout="vertical" onFinish={handleFinish} disabled={mutation.isPending} requiredMark={false}>
        <Form.Item
          name="currentPassword"
          label={t('account.changePassword.currentPassword')}
          rules={[{ required: true, message: t('account.changePassword.currentPassword') }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={t('account.changePassword.newPassword')}
          rules={[
            { required: true, message: t('account.changePassword.newPassword') },
            { min: 6, message: t('account.changePassword.tooShort') },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('account.changePassword.confirmPassword')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('account.changePassword.confirmPassword') },
            ({ getFieldValue }) => ({
              validator(_, value: string) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve()
                return Promise.reject(new Error(t('account.changePassword.mismatch')))
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
